import { ItemDTO, PrintedTicketLinkDTO } from '../models/order';

// The item fields the API sends that ItemDTO does not declare.
type ItemWithIds = ItemDTO & { ticket_id?: string; event_seat_id?: string | null };

const isItem = (value: unknown): value is ItemWithIds => typeof value === 'object'
  && value !== null
  && typeof (value as { id?: unknown }).id === 'string';

/**
 * The item a link or unlink answered with. The API used to send the item
 * itself and may wrap it as `{ item }` alongside the order's linking state,
 * so both shapes are read. null when neither carries an item.
 */
export const getPrintedLinkItem = (data: PrintedTicketLinkDTO | undefined | null): ItemWithIds | null => {
  if (!data) {
    return null;
  }
  const wrapped = (data as { item?: unknown }).item;
  if (isItem(wrapped)) {
    return wrapped;
  }
  return isItem(data) ? data : null;
};

// The relations come back only when the API includes them. The ones it leaves
// out are kept from the sold item when they are the same records (linking and
// unlinking copy the ticket type and the seat over), else the merge is unsafe.
const withRelationsOf = (previous: ItemDTO, next: ItemWithIds): ItemDTO | null => {
  let { ticket, event_seat: eventSeat } = next;
  if (!ticket) {
    if (next.ticket_id !== undefined && next.ticket_id !== previous.ticket?.id) {
      return null;
    }
    ticket = previous.ticket;
  }
  if (!eventSeat && next.event_seat_id !== null) {
    if (next.event_seat_id !== undefined && next.event_seat_id !== previous.event_seat?.id) {
      return null;
    }
    eventSeat = previous.event_seat;
  }
  return {
    ...previous, ...next, ticket, event_seat: eventSeat,
  };
};

/**
 * The order's item after linking `previous` to a printed ticket: the printed
 * one takes its place, under its own id. null when the answer is not enough
 * to tell, so the caller reloads the order instead.
 */
export const linkedItemFor = (previous: ItemDTO, data: PrintedTicketLinkDTO | undefined | null): ItemDTO | null => {
  const linked = getPrintedLinkItem(data);
  if (!linked || linked.is_printed !== true) {
    return null;
  }
  return withRelationsOf(previous, linked);
};

/**
 * The order's item after unlinking `previous`: a plain ticket comes back in
 * its place, waiting for another printed one. null when the answer is not
 * enough to tell.
 */
export const unlinkedItemFor = (previous: ItemDTO, data: PrintedTicketLinkDTO | undefined | null): ItemDTO | null => {
  const replacement = getPrintedLinkItem(data);
  if (!replacement || replacement.is_printed === true) {
    return null;
  }
  const merged = withRelationsOf(previous, replacement);
  // The printed ticket's number goes with it, back to its own order.
  return merged && { ...merged, display_id: replacement.display_id, is_printed: false };
};
